import React, { useState, useEffect } from "react";
import { formatDateWithoutHour } from "../utils/date.js";

/**
 * Componente para gerenciar boletos parcelados
 * Permite adicionar múltiplos boletos com data de vencimento específica para cada parcela
 */
const BoletosParceladosManager = ({ 
    numeroParcelas, 
    dataVencimentoPrimeira,
    intervalo,
    onBoletosChange,
    disabled = false,
    boletosExistentes = []
}) => {
    const [boletos, setBoletos] = useState(boletosExistentes.length > 0 ? boletosExistentes : []);

    // Estilo da seção - usando azul padrão do sistema
    const sectionStyle = {
        backgroundColor: "#EFF6FF",
        padding: "20px",
        borderRadius: "12px",
        marginTop: "20px",
        border: "2px solid #BFDBFE"
    };

    const titleStyle = {
        fontSize: "16px",
        fontWeight: "600",
        marginBottom: "16px",
        color: "#1E40AF",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    };

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: "16px",
        backgroundColor: "white",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    };

    const thStyle = {
        backgroundColor: "#DBEAFE",
        padding: "12px",
        textAlign: "left",
        fontWeight: "600",
        color: "#1E40AF",
        borderBottom: "2px solid #BFDBFE"
    };

    const tdStyle = {
        padding: "12px",
        borderBottom: "1px solid #E5E7EB"
    };

    const inputFileStyle = {
        display: "none"
    };

    const dateInputStyle = {
        padding: "8px 12px",
        borderRadius: "6px",
        border: "1px solid #D1D5DB",
        fontSize: "14px",
        fontFamily: "inherit",
        width: "150px"
    };

    // Calcular datas de vencimento baseadas no intervalo
    const calcularDataVencimento = (numeroParcela) => {
        if (!dataVencimentoPrimeira || !intervalo) return null;

        const dataBase = new Date(dataVencimentoPrimeira);
        let diasParaAdicionar = 0;

        switch(intervalo) {
            case "SEMANAL":
                diasParaAdicionar = (numeroParcela - 1) * 7;
                break;
            case "QUINZENAL":
                diasParaAdicionar = (numeroParcela - 1) * 15;
                break;
            case "MENSAL":
                // Para mensal, adicionar meses
                const novaData = new Date(dataBase);
                novaData.setMonth(novaData.getMonth() + (numeroParcela - 1));
                return novaData.toISOString().split('T')[0];
            case "BIMESTRAL":
                const novaDataBi = new Date(dataBase);
                novaDataBi.setMonth(novaDataBi.getMonth() + (numeroParcela - 1) * 2);
                return novaDataBi.toISOString().split('T')[0];
            case "TRIMESTRAL":
                const novaDataTri = new Date(dataBase);
                novaDataTri.setMonth(novaDataTri.getMonth() + (numeroParcela - 1) * 3);
                return novaDataTri.toISOString().split('T')[0];
            case "SEMESTRAL":
                const novaDataSem = new Date(dataBase);
                novaDataSem.setMonth(novaDataSem.getMonth() + (numeroParcela - 1) * 6);
                return novaDataSem.toISOString().split('T')[0];
            default:
                diasParaAdicionar = (numeroParcela - 1) * 30; // default mensal
        }

        const novaData = new Date(dataBase);
        novaData.setDate(novaData.getDate() + diasParaAdicionar);
        return novaData.toISOString().split('T')[0];
    };

    // Adicionar boleto para uma parcela específica
    const handleAddBoleto = (event, numeroParcela) => {
        const file = event.target.files[0];
        if (!file) return;

        const dataVencimento = calcularDataVencimento(numeroParcela);
        
        const novoBoleto = {
            numeroParcela,
            dataVencimento,
            arquivo: file,
            nomeArquivo: file.name,
            isNew: true
        };

        const novosBoletos = [...boletos.filter(b => b.numeroParcela !== numeroParcela), novoBoleto];
        setBoletos(novosBoletos);
        onBoletosChange(novosBoletos);
    };

    // Atualizar data de vencimento manualmente
    const handleDataVencimentoChange = (numeroParcela, novaData) => {
        const boletosAtualizados = boletos.map(boleto => {
            if (boleto.numeroParcela === numeroParcela) {
                return { ...boleto, dataVencimento: novaData };
            }
            return boleto;
        });
        
        setBoletos(boletosAtualizados);
        onBoletosChange(boletosAtualizados);
    };

    // Remover boleto
    const handleRemoveBoleto = (numeroParcela) => {
        const novosBoletos = boletos.filter(b => b.numeroParcela !== numeroParcela);
        setBoletos(novosBoletos);
        onBoletosChange(novosBoletos);
    };

    // Gerar linhas da tabela para cada parcela
    const renderLinhasParcelas = () => {
        const linhas = [];
        for (let i = 1; i <= numeroParcelas; i++) {
            const boletoExistente = boletos.find(b => b.numeroParcela === i);
            const dataVencimentoPadrao = calcularDataVencimento(i);
            const dataVencimentoAtual = boletoExistente?.dataVencimento || dataVencimentoPadrao;

            linhas.push(
                <tr key={i}>
                    <td style={tdStyle}>
                        <div style={{
                            display: "inline-block",
                            backgroundColor: "#DBEAFE",
                            color: "#1E40AF",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            fontWeight: "600",
                            fontSize: "14px"
                        }}>
                            Parcela {i}/{numeroParcelas}
                        </div>
                    </td>
                    <td style={tdStyle}>
                        {boletoExistente ? (
                            <input
                                type="date"
                                value={dataVencimentoAtual}
                                onChange={(e) => handleDataVencimentoChange(i, e.target.value)}
                                disabled={disabled}
                                style={{
                                    ...dateInputStyle,
                                    opacity: disabled ? 0.6 : 1,
                                    cursor: disabled ? "not-allowed" : "text"
                                }}
                            />
                        ) : (
                            <span style={{ color: "#9CA3AF" }}>
                                {dataVencimentoPadrao ? formatDateWithoutHour(dataVencimentoPadrao) : "N/A"}
                            </span>
                        )}
                    </td>
                    <td style={tdStyle}>
                        {boletoExistente ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ 
                                    color: "#10B981", 
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontSize: "13px"
                                }}>
                                    <span style={{ fontSize: "16px" }}>✓</span>
                                    {boletoExistente.nomeArquivo || boletoExistente.arquivo?.split('/').pop() || 'Boleto'}
                                </span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2 hover:bg-red-700 transition-colors"
                                        onClick={() => handleRemoveBoleto(i)}
                                    >
                                        <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Remover" />
                                        <span>Remover</span>
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div>
                                <input
                                    type="file"
                                    id={`boleto-parcela-${i}`}
                                    accept=".pdf"
                                    onChange={(e) => handleAddBoleto(e, i)}
                                    style={inputFileStyle}
                                    disabled={disabled}
                                />
                                <label 
                                    htmlFor={`boleto-parcela-${i}`}
                                    className={`inline-block ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                >
                                    <span className={`px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md flex items-center gap-2 hover:bg-blue-700 transition-colors ${disabled ? 'pointer-events-none' : ''}`}>
                                        <img className="w-4 h-4" src="/images/icon/upload.png" alt="Enviar" />
                                        <span>Enviar Boleto</span>
                                    </span>
                                </label>
                            </div>
                        )}
                    </td>
                </tr>
            );
        }
        return linhas;
    };

    // Calcular estatísticas
    const boletosAdicionados = boletos.length;
    const boletosFaltando = numeroParcelas - boletosAdicionados;
    const percentualCompleto = Math.round((boletosAdicionados / numeroParcelas) * 100);

    return (
        <div className="boletos-manager" style={sectionStyle}>
            <div style={titleStyle}>
                <span>📄</span>
                <span>Gerenciamento de Boletos Parcelados</span>
            </div>

            {/* Estatísticas */}
            <div className="boletos-stats" style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginBottom: "16px",
                padding: "16px",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
            }}>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#10B981" }}>
                        {boletosAdicionados}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Boletos Adicionados
                    </div>
                </div>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#F59E0B" }}>
                        {boletosFaltando}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Boletos Faltando
                    </div>
                </div>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#3B82F6" }}>
                        {percentualCompleto}%
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280" }}>
                        Progresso
                    </div>
                </div>
            </div>

            {/* Tabela de parcelas */}
            <table className="boletos-table" style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Parcela</th>
                        <th style={thStyle}>Vencimento</th>
                        <th style={thStyle}>Boleto</th>
                    </tr>
                </thead>
                <tbody>
                    {renderLinhasParcelas()}
                </tbody>
            </table>

            {/* Dica */}
            <div style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#DBEAFE",
                borderRadius: "6px",
                fontSize: "13px",
                color: "#1E40AF",
                border: "1px solid #BFDBFE"
            }}>
                <strong>💡 Dica:</strong> Adicione os boletos de cada parcela. As datas de vencimento são calculadas 
                automaticamente baseado no intervalo configurado ({intervalo ? intervalo.toLowerCase() : 'mensal'}), 
                mas você pode editá-las clicando no campo de data após adicionar o boleto.
            </div>

            <style>
                {`
                    @media (max-width: 640px) {
                        .boletos-manager {
                            padding: 12px !important;
                            margin-top: 12px !important;
                            overflow-x: hidden !important;
                        }

                        .boletos-stats {
                            gap: 8px !important;
                            padding: 10px !important;
                        }

                        .boletos-stats > div > div:first-child {
                            font-size: 20px !important;
                        }

                        .boletos-stats > div > div:last-child {
                            font-size: 11px !important;
                        }

                        .boletos-table {
                            display: block !important;
                        }

                        .boletos-table thead {
                            display: none !important;
                        }

                        .boletos-table tbody {
                            display: block !important;
                        }

                        .boletos-table tbody tr {
                            display: flex !important;
                            flex-wrap: wrap !important;
                            align-items: center !important;
                            gap: 8px !important;
                            padding: 12px !important;
                            border-bottom: 1px solid #E5E7EB !important;
                        }

                        .boletos-table tbody td {
                            padding: 4px 0 !important;
                            border: none !important;
                        }

                        /* Parcela badge */
                        .boletos-table tbody td:nth-child(1) {
                            flex: 0 0 auto !important;
                        }

                        /* Vencimento - date input */
                        .boletos-table tbody td:nth-child(2) {
                            flex: 1 1 auto !important;
                        }

                        .boletos-table tbody td:nth-child(2) input[type="date"] {
                            width: 100% !important;
                            min-width: 0 !important;
                        }

                        /* Boleto - file/actions */
                        .boletos-table tbody td:nth-child(3) {
                            flex: 0 0 100% !important;
                        }

                        .boletos-table tbody td:nth-child(3) > div {
                            flex-wrap: wrap !important;
                        }

                        .boletos-table tbody td:nth-child(3) span[style*="color: #10B981"] {
                            max-width: 100% !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default BoletosParceladosManager;
